/**
 * 将钱包 / RPC 抛出的英文技术错误转成用户可读文案（中/英）。
 * 去掉 MetaMask 等附带的 JSON 尾巴，避免整段代码式提示。
 */
import { isChineseLocale } from './lang/locales.mjs'
import { toTraditional } from './lang/s2t.mjs'

const JA_ERRORS = {
  '操作失败，请稍后重试': '操作に失敗しました。しばらくして再試行してください。',
  '主网币余额不足，无法支付手续费': 'ネイティブ通貨が不足しており、手数料を支払えません。',
  'Gas 不足或 Gas 上限过低': 'GAS が不足しているか、上限が低すぎます。',
  '交易序号异常，请稍后再试': 'トランザクション nonce が無効です。しばらくして再試行してください。',
  '有待确认交易或重复提交，请稍后再试': '保留中のトランザクションがある可能性があります。しばらくして再試行してください。',
  '合约执行未通过': 'スマートコントラクトがこのトランザクションを拒否しました。',
  '网络异常，请稍后重试': 'ネットワークエラーです。再試行してください。',
}

const KO_ERRORS = {
  '操作失败，请稍后重试': '작업에 실패했습니다. 잠시 후 다시 시도하세요.',
  '主网币余额不足，无法支付手续费': '네이티브 코인이 부족하여 수수료를 지불할 수 없습니다.',
  'Gas 不足或 Gas 上限过低': 'GAS가 부족하거나 한도가 너무 낮습니다.',
  '交易序号异常，请稍后再试': '트랜잭션 nonce가 유효하지 않습니다. 잠시 후 다시 시도하세요.',
  '有待确认交易或重复提交，请稍后再试': '대기 중인 트랜잭션이 있을 수 있습니다. 잠시 후 다시 시도하세요.',
  '合约执行未通过': '스마트 컨트랙트가 이 트랜잭션을 거부했습니다.',
  '网络异常，请稍后重试': '네트워크 오류입니다. 다시 시도하세요.',
}

const ES_ERRORS = {
  '操作失败，请稍后重试': 'Algo salió mal. Inténtalo de nuevo.',
  '主网币余额不足，无法支付手续费': 'Moneda nativa insuficiente para pagar las comisiones.',
  'Gas 不足或 Gas 上限过低': 'GAS insuficiente o límite de gas demasiado bajo.',
  '交易序号异常，请稍后再试': 'Nonce de transacción no válido. Espera un momento e inténtalo de nuevo.',
  '有待确认交易或重复提交，请稍后再试': 'Puede haber una transacción pendiente. Espera e inténtalo de nuevo.',
  '合约执行未通过': 'El contrato inteligente rechazó esta transacción.',
  '网络异常，请稍后重试': 'Error de red. Inténtalo de nuevo.',
}

const PT_BR_ERRORS = {
  '操作失败，请稍后重试': 'Algo deu errado. Tente novamente.',
  '主网币余额不足，无法支付手续费': 'Moeda nativa insuficiente para pagar as taxas.',
  'Gas 不足或 Gas 上限过低': 'GAS insuficiente ou limite de gas muito baixo.',
  '交易序号异常，请稍后再试': 'Nonce de transação inválido. Aguarde um momento e tente de novo.',
  '有待确认交易或重复提交，请稍后再试': 'Pode haver uma transação pendente. Aguarde e tente de novo.',
  '合约执行未通过': 'O contrato inteligente rejeitou esta transação.',
  '网络异常，请稍后重试': 'Erro de rede. Tente novamente.',
}

const VI_ERRORS = {
  '操作失败，请稍后重试': 'Có lỗi xảy ra. Vui lòng thử lại.',
  '主网币余额不足，无法支付手续费': 'Không đủ coin native để trả phí mạng.',
  'Gas 不足或 Gas 上限过低': 'GAS không đủ hoặc giới hạn gas quá thấp.',
  '交易序号异常，请稍后再试': 'Nonce không hợp lệ. Đợi một chút rồi thử lại.',
  '有待确认交易或重复提交，请稍后再试': 'Có thể có giao dịch đang chờ. Đợi rồi thử lại.',
  '合约执行未通过': 'Hợp đồng thông minh đã từ chối giao dịch này.',
  '网络异常，请稍后重试': 'Lỗi mạng. Vui lòng thử lại.',
}

const FR_ERRORS = {
  '操作失败，请稍后重试': "Une erreur s'est produite. Réessayez.",
  '主网币余额不足，无法支付手续费': 'Monnaie native insuffisante pour payer les frais.',
  'Gas 不足或 Gas 上限过低': 'GAS insuffisant ou limite de gas trop basse.',
  '交易序号异常，请稍后再试': 'Nonce invalide. Patientez et réessayez.',
  '有待确认交易或重复提交，请稍后再试': 'Une transaction peut être en attente. Patientez et réessayez.',
  '合约执行未通过': 'Le contrat intelligent a rejeté cette transaction.',
  '网络异常，请稍后重试': 'Erreur réseau. Réessayez.',
}

export function humanizeWalletError(raw, langCode = 'en') {
  const text = extractMessage(raw)
  const l = String(langCode || 'en')
  const pick = (zh, en) => {
    if (l === 'ja') return JA_ERRORS[zh] ?? en
    if (l === 'ko') return KO_ERRORS[zh] ?? en
    if (l === 'es') return ES_ERRORS[zh] ?? en
    if (l === 'pt-BR') return PT_BR_ERRORS[zh] ?? en
    if (l === 'vi') return VI_ERRORS[zh] ?? en
    if (l === 'fr') return FR_ERRORS[zh] ?? en
    if (!isChineseLocale(l)) return en
    return l === 'zh-TW' ? toTraditional(zh) : zh
  }

  const s = text.toLowerCase()
  if (!text.trim()) {
    return pick('操作失败，请稍后重试', 'Something went wrong. Please try again.')
  }

  if (/buffer is not defined|referenceerror.*buffer/.test(s)) {
    return pick('页面缺少 Buffer 兼容层，请刷新页面后重试', 'Buffer polyfill missing. Hard refresh the page and try again.')
  }

  if (/status code 429|too many requests|rate limit/.test(s)) {
    return pick('Tron 节点请求过于频繁，请等待几秒后重试', 'TRON node rate limit reached. Wait a few seconds and retry.')
  }

  if (/status code 400|triggersmartcontract|tron_tx_build_failed/.test(s)) {
    return pick('Tron 节点拒绝构建交易，请稍后重试或重新连接钱包', 'TRON node rejected the transaction. Retry or reconnect your wallet.')
  }

  if (
    /user denied|denied transaction|user rejected|rejected the request|rejected transaction|action_rejected|4001|cancelled|canceled|rejected signature|connection request reset|request reset|pairing cancelled|pairing canceled|modal closed/.test(
      s
    )
  ) {
    return pick('已在钱包关闭本次签名', 'You closed this step in your wallet. Try again whenever you like.')
  }

  if (/wallet_sign_unavailable/.test(s)) {
    return pick('无法发起签名，请重新选择钱包后再试', 'Could not start signing. Please pick your wallet again.')
  }

  if (/insufficient funds|insufficient balance for transaction|insufficient balance/.test(s)) {
    return pick('主网币余额不足，无法支付手续费', 'Insufficient native currency to pay network fees.')
  }

  if (/intrinsic gas too low|out of gas|gas required exceeds|exceeds block gas limit/.test(s)) {
    return pick('Gas 不足或 Gas 上限过低', 'Gas is too low or the gas limit is insufficient.')
  }

  if (/nonce too low|nonce has already been used|nonce too high/.test(s)) {
    return pick('交易序号异常，请稍后再试', 'Invalid transaction nonce. Please wait a moment and try again.')
  }

  if (/replacement transaction underpriced|already known|already imported/.test(s)) {
    return pick('有待确认交易或重复提交，请稍后再试', 'A pending transaction may be blocking this one. Please wait and retry.')
  }

  if (/execution reverted|revert|vm exception/.test(s)) {
    return pick('合约执行未通过', 'The smart contract reverted this transaction.')
  }

  if (/invalid sender|rpc submit.*invalid sender/i.test(s)) {
    return pick(
      '钱包无法提交交易（invalid sender）。请确认已切换到正确网络（如 FON Chain），且钱包当前账户与页面地址一致，然后重新连接后再试',
      'The wallet refused this transaction (invalid sender). Confirm the correct network, ensure the active wallet account matches the address shown here, then reconnect and retry.'
    )
  }

  if (/please call connect\(\) before request|wc_session_not_ready|wc_session_stale|pending session not found/.test(s)) {
    return pick(
      '钱包连接已失效，请重新扫码连接后再授权',
      'Wallet connection expired. Reconnect via QR code and try again.',
    )
  }

  const errCode = typeof raw === 'object' && raw != null ? String(raw.code || '') : ''
  const errObj = typeof raw === 'object' && raw != null ? raw : {}

  if (errCode === 'WC_WALLET_WRONG_CHAIN' || /wc_wallet_wrong_chain/.test(s)) {
    const need = errObj.chainName || pick('源链 BNB Chain', 'BNB Chain (source)')
    const on = errObj.walletChainName || pick('其他网络', 'another network')
    return pick(
      `手机钱包当前在「${on}」，授权须在「${need}」。请在 AVE 手动切到 ${need} 后再点授权`,
      `Your wallet is on ${on}, but approval must run on ${need}. Switch to ${need} in AVE, then approve again.`,
    )
  }

  if (errCode === 'TX_NOT_BROADCAST' || /tx_not_broadcast/.test(s)) {
    return pick(
      '钱包返回了哈希但交易未进入区块链（常见：手机未切到 BNB Chain）。请在 AVE 切到 BSC 后重试',
      'The wallet returned a hash but the transaction was never broadcast (often wrong network in AVE). Switch to BSC and retry.',
    )
  }

  if (errCode === 'NO_TRON_ACCOUNT' || /no_tron_account/.test(s)) {
    return pick(
      '请在 MetaMask 或 TronLink 中授权 TRON 账户',
      'Please authorize a TRON account in MetaMask or TronLink.',
    )
  }

  if (errCode === 'NO_METAMASK_TRON' || /no_metamask_tron/.test(s)) {
    return pick(
      '请更新 MetaMask 扩展至最新版以支持 TRON',
      'Update MetaMask to the latest version to use TRON.',
    )
  }

  if (/wallet is disconnected|wallet not connected|please connect first/i.test(s)) {
    return pick(
      'MetaMask TRON 连接已断开，请重新点击 MetaMask 并在扩展中授权 TRON 账户',
      'MetaMask TRON session ended. Click MetaMask again and authorize your TRON account.',
    )
  }

  if (errCode === 'WC_CHAIN_NOT_IN_SESSION' || /wc_chain_not_in_session/.test(s)) {
    const chainName = errObj.chainName || pick('源链', 'the source network')
    return pick(
      `钱包 session 未包含 ${chainName}。请断开后，选择 ${chainName} 为源链，再重新扫码连接`,
      `Wallet session does not include ${chainName}. Disconnect, select ${chainName} as the source chain, then scan the QR code again.`,
    )
  }

  if (errCode === 'WC_RELAY_TIMEOUT' || errCode === 'WC_ENABLE_TIMEOUT' || /wc_relay_timeout|wc_enable_timeout|wc_pairing_timeout/.test(s)) {
    return pick(
      'WalletConnect 中继连接失败，二维码无法生成。请检查网络/VPN，并在 cloud.reown.com 为项目添加 Allowed Domain：http://localhost:8080',
      'WalletConnect relay failed — QR cannot be generated. Check network/VPN and add http://localhost:8080 to Allowed Domains at cloud.reown.com.',
    )
  }

  if (/wc_send_tx_timeout|wc_switch_timeout|wc_add_chain_timeout/.test(s)) {
    return pick(
      '手机钱包未响应，请断开后重新扫码连接',
      'Your mobile wallet did not respond. Disconnect and reconnect via QR code.',
    )
  }

  if (/chain_switch_timeout/.test(s)) {
    return pick(
      '链切换超时：请在手机钱包切换到目标网络（如 BNB Chain）后重试',
      'Chain switch timed out. Switch to the target network in your mobile wallet and try again.',
    )
  }

  if (/wc_uri_timeout|wc_relay_timeout|publish custom payload|wc_init_timeout|wc_relay_unavailable/.test(s)) {
    return pick(
      'WalletConnect 二维码未生成：无法连接 relay 节点。请检查网络/代理，或在 DevTools → Network 确认 wss://relay.walletconnect.com 为 101',
      'WalletConnect QR was not generated: cannot reach the relay. Check network/proxy, or verify wss://relay.walletconnect.com shows status 101 in DevTools → Network.',
    )
  }

  if (/network|timeout|timed out|failed to fetch|econnreset|socket hang up|could not detect network/.test(s)) {
    return pick('网络异常，请稍后重试', 'Network error. Please try again.')
  }

  let cleaned = text.replace(/\s*\{[\s\S]*$/, '').replace(/\bhttps?:\/\/\S+/gi, '').replace(/\s+/g, ' ').trim()
  if (cleaned.length > 140) cleaned = `${cleaned.slice(0, 137)}…`
  if (!cleaned) return pick('操作失败，请稍后重试', 'Something went wrong. Please try again.')
  return cleaned
}

function extractMessage(raw) {
  if (raw == null) return ''
  if (typeof raw === 'string') return raw
  if (typeof raw === 'object' && raw.message != null) return String(raw.message)
  try {
    return JSON.stringify(raw)
  } catch {
    return String(raw)
  }
}
